import { type EntityState } from '@ngrx/entity';
import { type Draft } from 'immer';

import {
  type CanvasElement,
  type CanvasElementId,
  type ColumnId,
  type NewColumnPlacement,
  type PartialPropsOf,
  type SlotData,
  type SlotSizing,
  type WithColumns,
} from '@blaze-runtime/engine-domain';
import { deepClone } from '@blaze-shared/shared-util';

import { arePropsWithVisibility } from '../../canvas-elements/element-model/are-props-with-visibility';
import { isVariantOfContainer } from '../../canvas-elements/element-model/element-type-guards';
import { getElementSlotsCount } from '../../canvas-elements/element-model/slots';

/**
 * Result of inserting a new column.
 * Includes the element id and slot id of the added column.
 */
export interface InsertColumnResult {
  elementId: CanvasElementId;
  slotId?: ColumnId;
}

// TODO: test
export function insertColumnMutably(
  draft: Draft<EntityState<CanvasElement>>,
  elementId: CanvasElementId,
  slotId: ColumnId,
  columnPlacement: NewColumnPlacement,
  sizing: SlotSizing,
): InsertColumnResult {
  const element = draft.entities[elementId] as WithColumns | undefined;
  if (!element) {
    throw new Error('Could not find element');
  }

  const oldSlot = element.slots[slotId];
  if (!oldSlot) {
    throw new Error('Could not find old slot');
  }

  const slotIndex = element.slots.ids.indexOf(slotId);
  const oldSlotSizing = oldSlot.sizing;
  if (slotIndex === -1) {
    throw new Error('Could not find reference slot');
  }

  // Shrink old slot if that slot and the new one are 'percent'-sized
  if (
    oldSlotSizing?.mode === 'percent' &&
    oldSlotSizing.width !== undefined &&
    sizing.mode === 'percent'
  ) {
    oldSlotSizing.width -= sizing.width || 0;
    // For safety - limit the value
    if (oldSlotSizing.width <= 0) {
      oldSlotSizing.mode = 'fit-content';
      oldSlotSizing.width = undefined;
    }
  }

  const newSlotId = findFirstFreeColumnId(element.slots?.ids);

  // Define new column
  const newSlot: SlotData = { children: [], sizing, props: deepClone(oldSlot.props) };
  if (oldSlot.alignment) {
    newSlot.alignment = oldSlot.alignment;
  }

  element.slots[newSlotId] = newSlot;

  // Insert column into the desired place
  if (columnPlacement === 'before-slot') {
    element.slots.ids.splice(slotIndex, 0, newSlotId);
  } else if (columnPlacement === 'after-slot') {
    element.slots.ids.splice(slotIndex + 1, 0, newSlotId);
  }

  moveVisibilityFromContainerToSlotMutably(draft, elementId, newSlotId);

  return { elementId, slotId: newSlotId };
}

/**
 * Inserts a new empty slot into an element, directly *after* the slot at `index`.
 * @returns the id minted for the new slot, so callers can address it without reading it back.
 */
export function insertSlotMutably(
  draft: Draft<EntityState<CanvasElement>>,
  elementId: CanvasElementId,
  index: number,
): ColumnId {
  const element = draft.entities[elementId] as WithColumns | undefined;
  if (!element) {
    throw new Error('Could not find element');
  }

  const newSlotId = findFirstFreeColumnId(element.slots?.ids);
  const newSlot: SlotData = { children: [], props: {} };
  element.slots[newSlotId] = newSlot;
  element.slots.ids.splice(index + 1, 0, newSlotId);

  return newSlotId;
}

export function moveSlotMutably(
  draft: Draft<EntityState<CanvasElement>>,
  elementId: CanvasElementId,
  oldIndex: number,
  newIndex: number,
): void {
  const element = draft.entities[elementId] as WithColumns | undefined;
  if (!element) {
    throw new Error('Could not find element');
  }

  const [value] = element.slots.ids.splice(oldIndex, 1);
  element.slots.ids.splice(newIndex, 0, value);
}

// TODO: test
function findFirstFreeColumnId(ids: ColumnId[]): ColumnId {
  const numericIds = ids.map((id) => Number(id));
  const maxId = Math.max(0, ...numericIds) + 1;

  for (let i = 0; i <= maxId; i++) {
    if (!numericIds.includes(i)) {
      return `${i}`;
    }
  }

  throw new Error('Could not find first free id');
}

/**
 * When adding second column to container move visibility props to the first slot from main element.
 * @param draft
 * @param elementId
 * @param slotId
 */
function moveVisibilityFromContainerToSlotMutably(
  draft: Draft<EntityState<CanvasElement>>,
  elementId: CanvasElementId,
  slotId?: ColumnId,
): void {
  const element = draft.entities[elementId];

  if (!isVariantOfContainer(element)) {
    return;
  }

  const slotsCount = getElementSlotsCount(element);
  if (slotsCount !== 2) {
    return;
  }

  const props = element.props;
  if (!arePropsWithVisibility(props)) {
    return;
  }

  const targetSlotId = element.slots.ids.find((id) => id !== slotId);
  if (!targetSlotId) {
    return;
  }

  const draftSlot = element.slots[targetSlotId];
  if (!draftSlot) {
    return;
  }

  const draftSlotProps = draftSlot.props as PartialPropsOf<'container'>;
  let anyPropsWereCopied = false;

  // Move viewport visibility
  if (props.content.visible?.viewports) {
    draftSlotProps.content = {
      ...draftSlotProps.content,
      visible: {
        ...draftSlotProps.content?.visible,
        viewports: props.content.visible.viewports,
      },
    };
    delete props.content.visible.viewports;
    anyPropsWereCopied = true;
  }

  // Move expression visibility
  const visibilityDataProps = props.data?.find((data) => data.key === 'visible');
  if (visibilityDataProps) {
    draftSlotProps.content = {
      ...draftSlotProps.content,
      visible: {
        ...draftSlotProps.content?.visible,
        ...props.content.visible,
      },
      visibilityExpression:
        typeof visibilityDataProps.dataSource?.expression !== 'string'
          ? visibilityDataProps.dataSource?.expression
          : undefined,
    };

    delete props.content.visible;
    if (visibilityDataProps.dataSource?.expression) {
      visibilityDataProps.dataSource.expression = [];
    }
    visibilityDataProps.enabled = false;
    anyPropsWereCopied = true;
  }

  if (anyPropsWereCopied) {
    console.log(
      `[Elements] Copied visibility props from element: ${elementId} to it's slot ${targetSlotId}`,
    );
  }
}
